import React                                              from "react";
import styles                                             from './RootLayout.module.scss'
import { Menu, MenuItem, 
         ThemeProvider,
         THEME_ID as MATERIAL_THEME_ID
       }                                                  from "@mui/material"
import { useGlobalConfig }                                from "context/GlobalContext"
import { getTheme }                                       from "./Theme/theme";
import { AppBar, 
         Box, 
         IconButton, 
         Toolbar, 
         Typography 
       }                                                  from '@mui/material';
import CssBaseline                                        from '@mui/material/CssBaseline';
import AccountCircle                                      from "@mui/icons-material/AccountCircle";
import { useLocation }                                    from "react-router";
import ArrowBackIcon                                      from '@mui/icons-material/ArrowBack';
import { CssVarsProvider as JoyCssVarsProvider }          from '@mui/joy/styles';
import { DrawerLayout }                                   from "./drawer/DrawerLayout";
import { useHydratePilots } from "hooks/useHydratePilots";
import { useThemeMode } from 'context/ThemeContext';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import BedtimeOutlinedIcon from '@mui/icons-material/BedtimeOutlined';

interface RootLayoutProps {
  children: React.ReactNode
}

const expandedDrawerWidth = 270;
const collapsedDrawerWidth = 64;
const DRAWER_STATE_KEY = 'drawer_expanded_state';

export const RootLayout = ({children} : RootLayoutProps) => {
  const [drawerWidth, setDrawerWidth] = React.useState(expandedDrawerWidth);
  const [isDrawerExpanded, setIsDrawerExpanded] = React.useState(true);
  
  const location = useLocation()
  
  const { rc } = useGlobalConfig();
  const { session } = rc;
  const { mode, toggleTheme } = useThemeMode();
  let theme = getTheme(rc, mode);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState<null | HTMLElement>(null)
  const [openSE, setOpenSE] = React.useState(false)
  const [openIE, setOpenIE] = React.useState(false)

  // Hydrate pilots on app load
  useHydratePilots();

  // Load drawer state from localStorage on component mount
  React.useEffect(() => {
    try {
      const savedDrawerState = localStorage.getItem(DRAWER_STATE_KEY);
      if (savedDrawerState !== null) {
        const isExpanded = JSON.parse(savedDrawerState);
        setIsDrawerExpanded(typeof isExpanded === 'boolean' ? isExpanded : true);
        setDrawerWidth(isExpanded ? expandedDrawerWidth : collapsedDrawerWidth);
      } else {
        setIsDrawerExpanded(true);
        setDrawerWidth(expandedDrawerWidth);
      }
    } catch (e) {
      setIsDrawerExpanded(true);
      setDrawerWidth(expandedDrawerWidth);
    }
  }, []);

  const handleSEClose = (event : any, reason ?: string) => {
    if(event){
      rc.analytics.trackEvent(event, {reason});
    }
    if (reason && reason === 'backdropClick') return
    setOpenSE(false)
  }

  const handleIEClose = (event : any, reason ?: string) => {
    if(event){
      rc.analytics.trackEvent(event, {reason});
    }
    if (reason && reason === 'backdropClick') return
    setOpenIE(false)
  }

  const handeScheduleEvent = () => {
    rc.analytics.trackEvent('SCHEDULE_EVENT_BTN_CLICKED', {});
    setOpenSE(true)
  }

  const handeInstantanouesEVEvent = () => {
    rc.analytics.trackEvent('INSTANTANEOUS_EV_STOP_EVENT_BTN_CLICKED', {});
    setOpenIE(true)
  }

  const isMenuOpen = Boolean(anchorEl)
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl)

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null)
  }

  const handleLogout = () => {
    session.logoutUser();
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    handleMobileMenuClose()
  }

  const menuId = 'primary-search-account-menu'
  const renderMenu = (
    <Menu
      sx={{
        '& .MuiPaper-root': {
          minWidth: '150px'
        },
      }}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={handleLogout}>
        <Typography
        sx={{typography : {md : "bglBody1"}}}>
          Logout
        </Typography>
      </MenuItem>
      {/* <MenuItem onClick={handleMenuClose}>My account</MenuItem> */}
    </Menu>
  )

  const toggleDrawer = () => {
    const newExpandedState = !isDrawerExpanded;
    setIsDrawerExpanded(newExpandedState);
    setDrawerWidth(newExpandedState ? expandedDrawerWidth : collapsedDrawerWidth);
    
    // Save drawer state to localStorage
    localStorage.setItem(DRAWER_STATE_KEY, JSON.stringify(newExpandedState));
  };

  return (
    <ThemeProvider theme={{ [MATERIAL_THEME_ID]: theme }}>
      <React.Fragment>
        <Box sx={{ display: 'flex', height: '100vh', overflowY: 'hidden' }}>
          <JoyCssVarsProvider>
          <CssBaseline enableColorScheme/>
          <AppBar
            position="fixed"
            sx={{ 
              width: `calc(100% - ${drawerWidth}px)`, 
              transition: 'width 0.2s ease-in-out, margin-left 0.2s ease-in-out'
            }}>
            <Toolbar>
              <Typography variant="h6" noWrap component="div">
                {location.state?.title}
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Box sx={{ display: { xs: "none", md: "flex", alignItems: 'center' } }}>
                <IconButton
                  size="medium"
                  aria-label="toggle theme"
                  onClick={toggleTheme}
                  color="inherit"
                  sx={{ 
                    mr: 1,
                    borderRadius: '8px',
                    '& .MuiSvgIcon-root': {
                      fontSize: '18px'
                    },
                    '&:hover': {
                      backgroundColor: '#007f80',
                      color: 'white',
                      transition: 'all 0.2s ease-in-out'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  {mode === 'dark' ? <LightModeOutlinedIcon /> : <BedtimeOutlinedIcon />}
                </IconButton>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'end' }}>
                  {/* <Typography>
                    {rc.session.getUserInfo()?.name}
                  </Typography>
                  <Typography
                    variant="subtitle2">
                    {rc.permissionsManager.getUserRoleDescription()}
                  </Typography> */}
                </div>
                <IconButton
                  size="medium"
                  edge="end"
                  aria-label="account of current user"
                  aria-controls={menuId}
                  aria-haspopup="true"
                  onClick={handleProfileMenuOpen}
                  color="primary"
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      transform: 'scale(1.05)',
                      transition: 'all 0.2s ease-in-out'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <AccountCircle sx={{ fontSize: "24px" }} />
                </IconButton>
              </Box>
            </Toolbar>
          </AppBar>
        
          <DrawerLayout
            drawerWidth={drawerWidth}
            isExpanded={isDrawerExpanded}
            onToggle={toggleDrawer}
          />
        
          <Box
            component="main"
            sx={{ 
              flexGrow: 1, 
              height: '100vh', 
              overflowY: 'auto',
              transition: 'margin-left 0.2s ease-in-out',
              bgcolor: 'background.default',
            }}>
            <Toolbar />
            <div className={styles.mainCont}>{children}</div>
          </Box>
          </JoyCssVarsProvider>
        </Box>
        {renderMenu}
      </React.Fragment>
      
    </ThemeProvider>
  );
}